import { awaitSocketReceipt } from './connection-socket-receipt'
import { anyWsMessage, wsMessageRegistry } from '@rochadenis/axiomgo-contracts'
import { currentTurnLocale, currentTurnLocaleExplicit } from './turn-locale'
import {
  type ConnectionState,
  type TransportMode,
  type ConnectionConfig,
  DEFAULT_CONNECTION_CONFIG,
  type ChatRestRequest,
  type ChatRestResponse,
  type ServerEvent,
} from './shared-types'
import { useAuthStore } from '../shared/stores/auth'
import {
  SocketSessionLease,
  connectOwnedSocket,
  createAuthenticatedSocket,
  socketCloseDiagnostic,
} from './connection-socket-lease'
import { useEngineStore } from '../stores/engineStore'

const KNOWN_WS_TYPES = new Set<string>(Object.keys(wsMessageRegistry))

/**
 * STO-358: quantos pings seguidos sem `pong` a tela tolera antes de derrubar
 * o socket. Com `heartbeatMs: 20000`/`pingTimeoutMs: 20000` isso dá ~60s de
 * tolerância — cobre o turno longo que ocupa o motor sem confundir isso com
 * cano morto. Qualquer frame do servidor (não só `pong`) zera a contagem.
 */
const MAX_CONSECUTIVE_PING_FAILURES = 3

/**
 * Piso entre duas sondagens de WS feitas em modo DEGRADED (REST). Evita que
 * eventos de visibilidade/rede em rajada virem tempestade de handshakes.
 */
const MIN_PROBE_GAP_MS = 30_000

export type { ConnectionState, TransportMode }

export interface ConnectionEvents {
  onStateChange: (state: ConnectionState, transport: TransportMode) => void
  onMessage: (data: ServerEvent) => void
  onRawMessage: (data: unknown) => void
  /** Chamado a cada tentativa agendada de WS: count = n-ésima (1..max). */
  onRetryChange?: (count: number) => void
  onError: (error: Error, context: string) => void
}

/** Outgoing message stamped with a messageId for idempotency. */
interface OutgoingItem {
  messageId: string
  payload: Record<string, unknown>
}

export class ConnectionManager {
  private socketLease = new SocketSessionLease()
  private state: ConnectionState = 'DISCONNECTED'
  private transport: TransportMode = 'offline'
  private ws: WebSocket | null = null
  private retryCount = 0
  private retryTimer: ReturnType<typeof setTimeout> | null = null
  /** Sondagem silenciosa de WS em modo DEGRADED: uma tentativa só, sem churn de estado. */
  private quietProbe = false
  private lastProbeAt = 0
  private tokenRefreshTried = false
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null
  private healthCheckTimer: ReturnType<typeof setInterval> | null = null
  private pingTimeout: ReturnType<typeof setTimeout> | null = null
  /** Pings seguidos sem `pong` desde o último frame recebido ou reconexão (STO-358). */
  private consecutivePingFailures = 0
  private messageQueue: Array<{
    payload: ChatRestRequest
    resolve: (value: ChatRestResponse) => void
    reject: (error: Error) => void
  }> = []
  /** Outgoing WS messages queued while the socket is not yet OPEN. */
  private outgoingQueue: OutgoingItem[] = []
  /** messageIds already sent — prevents re-delivery after reconnect. */
  private sentMessageIds = new Set<string>()
  private config: ConnectionConfig
  private events: ConnectionEvents
  private tenantId = ''
  private userId = ''

  constructor(config: Partial<ConnectionConfig>, events: ConnectionEvents) {
    this.config = { ...DEFAULT_CONNECTION_CONFIG, ...config }
    this.events = events
  }

  // ═══ PUBLIC API ═══

  /**
   * Expose outgoing queue for test inspection.
   * Do NOT call in production code — internal implementation detail.
   */
  getOutgoingQueue(): OutgoingItem[] {
    return [...this.outgoingQueue]
  }

  /**
   * Inject a pre-built item directly into the outgoing queue.
   * Test-only helper — allows asserting idempotency without going through sendRaw.
   */
  injectOutgoingQueueItem(item: OutgoingItem): void {
    this.outgoingQueue.push(item)
  }

  connect(tenantId: string, userId: string): void {
    this.destroy()
    this.socketLease.start(tenantId, userId)
    this.tenantId = tenantId
    this.userId = userId

    if (!tenantId || !userId) {
      console.warn('[ConnectionManager] tenantId ou userId vazio — não conectando')
      return
    }

    this.setState('CONNECTING', 'websocket')
    // Fase 2 — Camada A capability handshake (SPEC v5.0 §2 PR-B.4, A1/A15, R3-9):
    // MOUNT-ONLY — connect() is the ONLY live entrypoint (App.tsx → initialize).
    // Reconnections call connectWs() directly, so the probe never re-fires.
    // Fire-and-forget so it never blocks the WS handshake; FAIL-OPEN inside.
    void this.probeCapabilities()
    this.connectWs()

    window.addEventListener('online', this.handleOnline)
    window.addEventListener('offline', this.handleOffline)
    document.addEventListener('visibilitychange', this.handleVisibility)
  }

  /**
   * Read `/health.preventivePlanBuilder` once at boot and cache it in the canonical
   * engine store (SPEC v5.0 §2 PR-B.4 / A2). `/health` is NON-authenticated and
   * does NOT read tenant/user headers (engine `health.ts:55-78`), so the probe
   * sends NO `X-Tenant-Id`/`X-User-Id` (R3-4/R3-12). FAIL-OPEN: any failure leaves
   * `engineCapabilities` at `{}` (the gate then defaults to showing the button).
   */
  private async probeCapabilities(): Promise<void> {
    try {
      const res = await fetch(`${this.config.restUrl}/health`, {
        signal: AbortSignal.timeout(5000),
      })
      if (!res.ok) return // engine indisponível → fail-open (não grava)
      const health = (await res.json()) as { preventivePlanBuilder?: boolean }
      // FAIL-OPEN for "engine velho sem o campo" (INV-9 / risk table): only cache
      // the capability when the field is actually PRESENT. A missing field must
      // leave engineCapabilities at {} so the gate stays fail-open (button shows);
      // only an explicit `false` hides the button (engine reported OFF).
      if (typeof health.preventivePlanBuilder !== 'boolean') return
      useEngineStore.getState().setEngineCapabilities({
        preventivePlanBuilder: health.preventivePlanBuilder,
      })
    } catch {
      // não-JSON / timeout / engine velho sem o campo → engineCapabilities fica {}.
    }
  }

  disconnect(): void {
    this.cleanup()
    this.setState('DISCONNECTED', 'offline')
  }

  async send(content: string, conversationId?: string): Promise<ChatRestResponse> {
    if (!this.socketLease.allowsUse()) throw new Error('Connection identity changed')
    const payload: ChatRestRequest = {
      content,
      tenantId: this.tenantId,
      userId: this.userId,
      conversationId,
    }

    if (this.transport === 'websocket' && this.ws?.readyState === WebSocket.OPEN) {
      return this.sendViaWs(payload)
    }

    if (this.transport === 'rest') {
      return this.sendViaRest(payload)
    }

    // Offline — queue
    return new Promise((resolve, reject) => {
      this.messageQueue.push({ payload, resolve, reject })
      console.log(`[ConnectionManager] mensagem na fila offline (${this.messageQueue.length})`)
    })
  }

  /** Send raw JSON over WebSocket (for existing protocol compatibility) */
  sendRaw(data: unknown): boolean {
    if (!this.socketLease.allowsUse()) return false
    // Reject messages without a valid type — backend will reject them
    const msgType =
      typeof data === 'object' && data !== null
        ? (data as Record<string, unknown>)['type']
        : undefined
    if (!msgType || msgType === 'unknown') {
      console.warn('[ConnectionManager] sendRaw blocked: message has no valid type', data)
      return false
    }

    // Always inject userId and tenantId so no caller can forget them
    const payload: Record<string, unknown> = {
      ...(data as Record<string, unknown>),
      userId: this.userId,
      tenantId: this.tenantId,
    }

    if (this.ws?.readyState === WebSocket.OPEN) {
      const messageId = this.generateMessageId()
      this.sentMessageIds.add(messageId)
      payload['messageId'] = messageId
      this.ws.send(JSON.stringify(payload))
      return true
    }

    // Not connected — enqueue for delivery after reconnect
    const messageId = this.generateMessageId()
    this.outgoingQueue.push({ messageId, payload })
    console.log(`[ConnectionManager] outgoing queued (${this.outgoingQueue.length}):`, msgType)
    return false
  }

  getState(): { state: ConnectionState; transport: TransportMode; retryCount: number } {
    return { state: this.state, transport: this.transport, retryCount: this.retryCount }
  }

  getWebSocket(): WebSocket | null {
    return this.ws
  }

  // ═══ PRIVATE — WebSocket ═══

  private connectWs(): void {
    connectOwnedSocket(this.socketLease, this.tenantId, this.userId, (token) =>
      this.doConnectWs(token),
    )
  }

  private generateMessageId(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID()
    }
    // Polyfill — only reached in environments without crypto.randomUUID (old Node/jsdom)
    const hex = () =>
      Math.floor(Math.random() * 0x10000)
        .toString(16)
        .padStart(4, '0')
    return `${hex()}${hex()}-${hex()}-4${hex().slice(1)}-${((parseInt(hex(), 16) & 0x3f) | 0x80).toString(16).padStart(4, '0')}-${hex()}${hex()}${hex()}`
  }

  private doConnectWs(token: string | null): void {
    // Solta o socket anterior: sem isso o onclose dele dispara handleWsFailure
    // de novo e multiplica as tentativas.
    if (this.ws) {
      this.ws.onopen = null
      this.ws.onclose = null
      this.ws.onerror = null
      this.ws.onmessage = null
      try {
        this.ws.close()
      } catch {
        /* já fechado */
      }
      this.ws = null
    }
    try {
      this.ws = createAuthenticatedSocket(this.config.wsUrl, token)
    } catch {
      this.handleWsFailure()
      return
    }

    const ws = this.ws
    let opened = false
    const ownsSubject = this.socketLease.capture(this.tenantId, this.userId)
    const current = () => this.ws === ws && ownsSubject()

    ws.onopen = () => {
      if (!current()) return
      opened = true
      console.log('[ConnectionManager] WS conectado')
      this.quietProbe = false
      this.tokenRefreshTried = false
      if (this.healthCheckTimer) {
        clearInterval(this.healthCheckTimer)
        this.healthCheckTimer = null
      }
      this.setState('CONNECTED', 'websocket')
      this.resetRetry()
      this.startHeartbeat()
      this.drainOutgoingQueue()
      this.flushQueue()
    }

    ws.onclose = (event) => {
      if (!current()) return
      console.log(
        '[ConnectionManager] WS fechou',
        socketCloseDiagnostic(event, this.config.wsUrl, opened, this.retryCount + 1),
      )
      this.stopHeartbeat()

      // 4001 = token expired — refresh token and reconnect
      // Só UMA renovação por sequência de falhas: servidor que responde 4001
      // sempre não pode prender o cliente num laço sem espera.
      if (event.code === 4001 && !this.tokenRefreshTried) {
        console.log('[ConnectionManager] Token expired — refreshing and reconnecting')
        this.tokenRefreshTried = true
        this.connectWs()
        return
      }

      this.handleWsFailure()
    }

    ws.onerror = () => {
      // onclose already handles — don't duplicate
    }

    ws.onmessage = (event) => {
      if (current()) this.handleWsMessage(event)
    }
  }

  private handleWsMessage(event: MessageEvent): void {
    // Qualquer frame do servidor — parseável ou não — prova que o cano está
    // vivo: zera o contador de falhas consecutivas de ping (STO-358). Mesmo
    // um frame truncado passou pelo fio, então o motor não está mudo.
    this.consecutivePingFailures = 0

    try {
      const data = JSON.parse(event.data as string) as Record<string, unknown>

      // Handle pong/heartbeat_ack from shared protocol
      if (data['type'] === 'pong' || data['type'] === 'heartbeat_ack') {
        this.clearPingTimeout()
        return
      }

      // Contract-level validation for known WS message types.
      // Only enforces schema for msg types declared in @rochadenis/axiomgo-contracts
      // (today: card:import:state-update). Unknown types propagate untouched
      // until more contracts land — strict validation expands as the registry grows.
      const msgType = typeof data['type'] === 'string' ? data['type'] : undefined
      if (msgType && KNOWN_WS_TYPES.has(msgType)) {
        const parsed = anyWsMessage.safeParse(data)
        if (!parsed.success) {
          console.warn('[ConnectionManager] WS msg rejected by contract schema', {
            type: msgType,
            issues: parsed.error.issues,
            raw: JSON.stringify(data).slice(0, 200),
          })
          return
        }
      }

      // Forward to both handlers — raw for existing protocol, typed for new
      this.events.onRawMessage(data)
      this.events.onMessage(data as unknown as ServerEvent)
    } catch (err) {
      // P1 Impeccable finding 3: a garbled/truncated frame used to be swallowed
      // with only this console.warn, so the chat silently stopped updating. The
      // connection is still OPEN — this is "the client dropped a piece of data",
      // NOT "the engine is down" (that is the connection-state machine's job).
      // Surface it through onError so the store can signal the user (distinct
      // from the ConnectionStatus indicator).
      console.warn('[ConnectionManager] mensagem WS inválida:', event.data)
      this.events.onError(
        err instanceof Error ? err : new Error('ws_message_unparseable'),
        'ws_message_dropped',
      )
    }
  }

  private handleWsFailure(): void {
    if (this.quietProbe) {
      // Sondagem falhou: segue quieto no REST, sem RECONNECTING nem contador.
      this.quietProbe = false
      if (this.state !== 'DEGRADED') this.setState('DEGRADED', 'rest')
      return
    }
    if (this.retryCount < this.config.maxWsRetries) {
      this.setState('RECONNECTING', 'websocket')
      this.scheduleRetry()
    } else {
      this.fallbackToRest()
    }
  }

  private scheduleRetry(): void {
    const delay =
      Math.min(this.config.baseRetryMs * Math.pow(2, this.retryCount), this.config.maxRetryMs) +
      Math.random() * 1000

    this.retryCount++
    this.events.onRetryChange?.(this.retryCount)
    console.log(
      `[ConnectionManager] retry ${this.retryCount}/${this.config.maxWsRetries} em ${Math.round(delay)}ms`,
    )

    if (this.retryTimer) clearTimeout(this.retryTimer)
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null
      this.connectWs()
    }, delay)
  }

  private startHeartbeat(): void {
    // Reconexão bem-sucedida zera a contagem de falhas (STO-358).
    this.consecutivePingFailures = 0

    this.heartbeatTimer = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.sendRaw({ type: 'ping', timestamp: Date.now() })

        // Clear any previous ping timeout to prevent leaked timers
        this.clearPingTimeout()

        this.pingTimeout = setTimeout(() => {
          this.consecutivePingFailures++

          // STO-358: o turno longo ocupa o motor e atrasa o pong sem o cano
          // estar morto. Só derruba no 3º ping seguido sem resposta — antes
          // disso, o próximo heartbeat tenta de novo naturalmente.
          if (this.consecutivePingFailures < MAX_CONSECUTIVE_PING_FAILURES) {
            console.warn('[ConnectionManager] heartbeat sem pong — falha consecutiva', {
              consecutivePingFailures: this.consecutivePingFailures,
            })
            return
          }

          const retryCount = this.retryCount
          console.warn('[ConnectionManager] heartbeat timeout — forcing reconnect', {
            retryCount,
            consecutivePingFailures: this.consecutivePingFailures,
          })
          // Sentry breadcrumb — fire-and-forget, non-blocking
          try {
            // Dynamic import avoids circular dependency and keeps Sentry optional
            import('./sentry')
              .then(({ captureException }) => {
                captureException(new Error('ws_heartbeat_timeout'), {
                  tags: { retryCount: String(retryCount) },
                })
              })
              .catch(() => {
                /* sentry not available */
              })
          } catch {
            // ignore
          }
          this.ws?.close()
        }, this.config.pingTimeoutMs)
      }
    }, this.config.heartbeatMs)
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer) clearInterval(this.heartbeatTimer)
    this.heartbeatTimer = null
    this.clearPingTimeout()
  }

  private clearPingTimeout(): void {
    if (this.pingTimeout) clearTimeout(this.pingTimeout)
    this.pingTimeout = null
  }

  /** Drain outgoing queue FIFO after WS reconnects. Idempotent: skips messageIds already sent. */
  private drainOutgoingQueue(): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return

    const pending = this.outgoingQueue.splice(0)
    for (const item of pending) {
      if (!this.socketLease.allowsUse()) return
      if (this.sentMessageIds.has(item.messageId)) {
        console.log('[ConnectionManager] outgoing skip (already sent):', item.messageId)
        continue
      }
      this.sentMessageIds.add(item.messageId)
      item.payload['messageId'] = item.messageId
      this.ws.send(JSON.stringify(item.payload))
    }
  }

  // ═══ PRIVATE — REST Fallback ═══

  private async getAuthHeaders(): Promise<Record<string, string>> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Tenant-Id': this.tenantId,
      'X-User-Id': this.userId,
    }
    const token = await useAuthStore.getState().getToken()
    if (token) headers['Authorization'] = `Bearer ${token}`
    return headers
  }

  private async fallbackToRest(): Promise<void> {
    console.log('[ConnectionManager] todas tentativas WS falharam — fallback REST')
    this.setState('DEGRADED', 'rest')

    try {
      const res = await fetch(`${this.config.restUrl}/ping`, {
        headers: { 'X-Tenant-Id': this.tenantId, 'X-User-Id': this.userId },
        signal: AbortSignal.timeout(5000),
      })
      if (res.status === 401 || res.status === 403) {
        console.warn(`[ConnectionManager] ping retornou ${res.status} — AUTH_ERROR, sem retry`)
        this.setState('AUTH_ERROR', 'offline')
        return
      }
      if (res.ok) {
        console.log('[ConnectionManager] REST disponível — modo DEGRADED ativo')
        this.startHealthCheckPolling()
        this.flushQueue()
      } else {
        this.goOffline()
      }
    } catch {
      this.goOffline()
    }
  }

  private async sendViaWs(payload: ChatRestRequest): Promise<ChatRestResponse> {
    const socket = this.ws
    if (!socket || socket.readyState !== WebSocket.OPEN)
      throw new Error('WebSocket não está aberto')
    return awaitSocketReceipt(socket, this.socketLease.snapshotOwner(), () =>
      this.sendRaw({ type: 'chat_message', ...payload }),
    )
  }

  private async sendViaRest(payload: ChatRestRequest): Promise<ChatRestResponse> {
    const ownsSubject = this.socketLease.snapshotOwner()
    const headers = await this.getAuthHeaders()
    if (!ownsSubject()) throw new Error('Connection identity changed')
    // Bloco 5 (2026-08-20) — a porta REST manda o MESMO `locale` que a porta
    // SSE (`chat-stream.ts`). As duas leem de `currentTurnLocale()`, um lugar
    // só: se só uma mandasse, o produto ficaria bilíngue por canal. STO-1401/
    // 1479: mesma regra para `localeExplicit` — via `currentTurnLocaleExplicit()`.
    const turnLocale = currentTurnLocale()
    const localeExplicit = turnLocale && currentTurnLocaleExplicit()
    const res = await fetch(`${this.config.restUrl}/api/v1/chat/message`, {
      method: 'POST',
      headers,
      body: JSON.stringify(
        turnLocale
          ? { ...payload, locale: turnLocale, ...(localeExplicit ? { localeExplicit: true } : {}) }
          : payload,
      ),
      signal: AbortSignal.timeout(30_000),
    })

    if (!ownsSubject()) throw new Error('Connection identity changed')
    if (res.status === 401 || res.status === 403) {
      console.warn(`[ConnectionManager] sendViaRest ${res.status} — transitioning to AUTH_ERROR`)
      this.setState('AUTH_ERROR', 'offline')
      throw new Error(`HTTP ${res.status}`)
    }

    if (!res.ok) {
      const err = await res.json().catch(() => ({ message: 'Erro desconhecido' }))
      if (!ownsSubject()) throw new Error('Connection identity changed')
      throw new Error((err as Record<string, string>).message || `HTTP ${res.status}`)
    }

    const receipt = (await res.json()) as ChatRestResponse
    if (!ownsSubject()) throw new Error('Connection identity changed')
    return receipt
  }

  private startHealthCheckPolling(): void {
    if (this.healthCheckTimer) clearInterval(this.healthCheckTimer)
    this.healthCheckTimer = setInterval(() => this.probeWsQuietly(), this.config.healthCheckMs)
  }

  /** Uma única tentativa de WS enquanto está em DEGRADED; sucesso volta a CONNECTED. */
  private probeWsQuietly(): void {
    if (this.state !== 'DEGRADED') return
    const now = Date.now()
    if (now - this.lastProbeAt < MIN_PROBE_GAP_MS) return
    this.lastProbeAt = now
    this.quietProbe = true
    this.connectWs()
  }

  private handleVisibility = (): void => {
    if (document.visibilityState === 'visible') this.probeWsQuietly()
  }

  // ═══ PRIVATE — Offline Mode ═══

  private goOffline(): void {
    console.log('[ConnectionManager] sem conexão — modo OFFLINE')
    this.setState('OFFLINE', 'offline')
  }

  private handleOnline = (): void => {
    console.log('[ConnectionManager] internet voltou — reconectando')
    this.retryCount = 0
    this.tokenRefreshTried = false
    this.quietProbe = false
    if (this.retryTimer) clearTimeout(this.retryTimer)
    this.retryTimer = null
    this.setState('CONNECTING', 'websocket')
    this.connectWs()
  }

  private handleOffline = (): void => {
    console.log('[ConnectionManager] internet caiu — modo OFFLINE')
    this.stopHeartbeat()
    if (this.ws) this.ws.close()
    this.goOffline()
  }

  private async flushQueue(): Promise<void> {
    while (this.messageQueue.length > 0 && this.socketLease.allowsUse()) {
      const item = this.messageQueue.shift()
      if (!item) break

      try {
        let result: ChatRestResponse
        if (this.transport === 'websocket' && this.ws?.readyState === WebSocket.OPEN) {
          result = await this.sendViaWs(item.payload)
        } else if (this.transport === 'rest') {
          result = await this.sendViaRest(item.payload)
        } else {
          this.messageQueue.unshift(item)
          break
        }
        item.resolve(result)
      } catch (error) {
        item.reject(error instanceof Error ? error : new Error('Falha ao enviar'))
      }
    }
  }

  // ═══ PRIVATE — State Machine ═══

  private setState(newState: ConnectionState, transport?: TransportMode): void {
    const prev = this.state
    this.state = newState
    if (transport) this.transport = transport
    console.log(`[ConnectionManager] ${prev} → ${newState} (${this.transport})`)
    this.events.onStateChange(newState, this.transport)
  }

  private resetRetry(): void {
    this.retryCount = 0
    if (this.retryTimer) {
      clearTimeout(this.retryTimer)
      this.retryTimer = null
    }
  }

  // ═══ CLEANUP ═══

  private cleanup(): void {
    this.socketLease.stop()
    this.stopHeartbeat()
    if (this.retryTimer) clearTimeout(this.retryTimer)
    if (this.healthCheckTimer) clearInterval(this.healthCheckTimer)
    window.removeEventListener('online', this.handleOnline)
    window.removeEventListener('offline', this.handleOffline)
    document.removeEventListener('visibilitychange', this.handleVisibility)
    if (this.ws) {
      this.ws.onopen = null
      this.ws.onclose = null
      this.ws.onerror = null
      this.ws.onmessage = null
      this.ws.close()
      this.ws = null
    }
    this.retryTimer = null
    this.healthCheckTimer = null
  }

  destroy(): void {
    this.cleanup()
    this.outgoingQueue = []
    this.messageQueue.forEach((item) => item.reject(new Error('ConnectionManager destroyed')))
    this.messageQueue = []
  }
}
